import * as React from 'react';

import * as LabelPrimitive from '@radix-ui/react-label';
import { type VariantProps, cva } from 'class-variance-authority';

import RequiredHint from 'components/RequiredHint';
import { cn } from 'utils/shadcn';

import AIHintLabel, { AIHintProps } from './AIHint';

const labelVariants = cva(
  'leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70'
);

const Label = React.forwardRef<
  React.ElementRef<typeof LabelPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof LabelPrimitive.Root> &
    VariantProps<typeof labelVariants> &
    AIHintProps & { required?: boolean; showAIHint?: boolean } // AIHintProps.name can be empty string to skip AI label
>(
  (
    {
      className,
      showAIHint = true,
      hideAIHint,
      aiIconOnly,
      readOnly,
      aiHintTooltip,
      ...props
    },
    ref
  ) => {
    // Memoize RequiredHint to prevent re-creation on every render
    const requiredHint = React.useMemo(() => {
      if (!props.required) return null;
      return <RequiredHint />;
    }, [props.required]);

    return (
      <LabelPrimitive.Root
        ref={ref}
        className={cn(labelVariants(), 'text-neutral-800')}
        {...props}
      >
        <span
          className={cn(
            'flex flex-row items-center text-sm whitespace-nowrap text-neutral-900',
            className
          )}
        >
          {props.children}
          {requiredHint}
          {props.name && showAIHint && (
            <AIHintLabel
              name={props.name}
              hideAIHint={hideAIHint}
              aiIconOnly={aiIconOnly}
              readOnly={readOnly}
              aiHintTooltip={aiHintTooltip}
            />
          )}
        </span>
      </LabelPrimitive.Root>
    );
  }
);
Label.displayName = LabelPrimitive.Root.displayName;

export { Label };
